/**
 * CommonStepper
 * Generic horizontal stepper used by Alert, VDI, and CoolerTransaction expanded panels.
 * Nodes are clickable; when `detailsContent` is provided the panel expands below the track.
 */
import React, { useState } from 'react';
import Tooltip from '@/components/atoms/Tooltip';
import { FaCheckDouble } from 'react-icons/fa';
import { FiX, FiChevronUp, FiCheck, FiClock, FiAlertCircle, FiLoader } from 'react-icons/fi';
import { getDateTime } from '@/utils/dateTime';
import { APP_BG, APP_BORDER, APP_HEX, APP_VDI } from '@/constant/theme/colors.constant';
import { useTheme } from '@/context/theme.context';
import AppButton from '@/components/atoms/AppButton';
import { Text } from '@/components/atoms/AppText';
import { ELEMENTS_PROPS } from '@/constant/global/global.constant';

// ─── Duration helper ─────────────────────────────────────────────────────────
const calcDuration = (from?: string | null, to?: string | null): string | undefined => {
  if (!from || !to || from === '-' || to === '-') return undefined;
  let diff = (new Date(to).getTime() - new Date(from).getTime()) / 1000;
  if (isNaN(diff)) return undefined;
  if (diff < 0) diff = 0; // Negative difference due to fractional seconds or slight out-of-order timestamps
  if (diff < 60) return `${Math.round(diff)}s`;
  const m = Math.floor(diff / 60); const s = Math.round(diff % 60);
  if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60); const rm = m % 60;
  return rm > 0 ? `${h}hr ${rm}m` : `${h}hr`;
};

// ─── Types ────────────────────────────────────────────────────────────────────

/** Explicit step state — takes priority over iconColor inference when provided */
export type StepStatus = 'done' | 'active' | 'failed' | 'not-reached';

export interface BaseStepperStep {
  title: string;
  /** ISO date string shown below the node label */
  date?: string;
  /** Hex colour string used to fill the node circle */
  iconColor?: string;
  /** Explicit status — when set, overrides iconColor-based colour and drives a themed colour */
  status?: StepStatus;
  iconElement?: React.ReactNode;
  /** Tooltip shown on hover (ReactNode for rich content, string for plain) */
  tooltipContent?: React.ReactNode;
  /** JSX rendered inside the detail panel body when the node is clicked */
  detailsContent?: React.ReactNode;
  /** When true the step is hidden from the track */
  excluded?: boolean;
  /** Renders a double-tick badge on the top-right of the node circle */
  doubleTick?: boolean;
  doubleTickColor?: string;
  /**
   * Arbitrary corner badge rendered top-right of the circle
   * (e.g. a status icon for VDI — FiCheckCircle / FiXCircle / FiLoader)
   */
  statusBadge?: React.ReactNode;
  /**
   * Human-readable duration shown above the connector rail to the right of
   * this step — e.g. "40s", "1m 10s". Computed by the caller.
   */
  durationToNext?: string;
  /** Small badge text shown below the title — e.g. "VMS", "MMS" */
  subtitle?: string;
}

interface CommonStepperProps {
  steps: BaseStepperStep[];
  emptyMessage?: string;
  emptySubMessage?: string;
  /** Custom legend items. Each entry has a display label and a hex color dot.
   *  Defaults to: Done / Active / Failed / Pending */
  legendItems?: Array<{ label: string; color: string }>;
  /** Show the ping ring + spin animation on active nodes. Default: true */
  showActiveAnimation?: boolean;
  /** Replace the active node icon with FiClock. Default: true */
  showActiveClockIcon?: boolean;
}

// ─── CommonStepper ────────────────────────────────────────────────────────────

export const CommonStepper = ({
  steps,
  emptyMessage,
  emptySubMessage,
  legendItems,
  showActiveAnimation = true,
  showActiveClockIcon = true,
}: CommonStepperProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { colors } = useTheme();

  const visibleSteps = steps.filter((s) => !s.excluded);
  const n = visibleSteps.length;

  /** Resolve the fill hex for a step — explicit status wins over iconColor */
  const resolveNodeColor = (step: BaseStepperStep): string => {
    if (step.status) {
      if (step.status === 'done') return colors.primary;
      if (step.status === 'active') return APP_HEX.warning;    // #f59e0b amber
      if (step.status === 'failed') return APP_VDI.failed;     // #E75065
      /* not-reached */                   return APP_HEX.slate400;   // #94a3b8
    }
    return step.iconColor ?? colors.primary;
  };

  if (!n) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3">
        <div
          className={`w-full max-w-sm px-6 py-5 rounded-2xl border border-dashed ${APP_BORDER.default} text-center`}
        >
          <Text as="p" size="base" weight="semibold" color="soft">
            {emptyMessage ?? ELEMENTS_PROPS.STEPPER.EMPTY_MESSAGE}
          </Text>
          {emptySubMessage && (
            <Text as="p" size="sm" color="muted" className="mt-1">
              {emptySubMessage}
            </Text>
          )}
        </div>
      </div>
    );
  }

  const handleClick = (i: number) =>
    setOpenIndex((prev) => (prev === i ? null : i));

  return (
    <div className="w-full flex flex-col gap-2 sm:gap-3 px-2 sm:px-4 py-1 sm:py-3">
      {/* ── Step track — scrollable on small screens ── */}
      <div className="overflow-x-auto scrollbar-none -mx-2 sm:mx-0 px-2 sm:px-0 py-1 sm:py-3">
        <div className="relative flex items-start justify-between w-full pt-2 sm:pt-3 pb-1 sm:pb-2">
          {/* No base rail — every segment is rendered explicitly below */}

          {visibleSteps.map((step, index) => {
            const isOpen = openIndex === index;
            const nodeColor = resolveNodeColor(step);
            const isNotReached = step.status === 'not-reached';

            return (
              <React.Fragment key={index}>
                {/* Segment line + duration pill */}
                {index > 0 && (() => {
                  const prevStep = visibleSteps[index - 1];
                  // mobile inset: radius(24) + 4px gap = 28px
                  // sm+ inset:   radius(28) + 4px gap = 32px  (longer line on web/tablet)
                  const segLeftMobile = `calc(${((2 * index - 1) / (2 * n)) * 100}% + 28px)`;
                  const segRightMobile = `calc(${((2 * (n - index) - 1) / (2 * n)) * 100}% + 28px)`;
                  const segLeftSm = `calc(${((2 * index - 1) / (2 * n)) * 100}% + 32px)`;
                  const segRightSm = `calc(${((2 * (n - index) - 1) / (2 * n)) * 100}% + 32px)`;
                  const railClsMobile = 'absolute z-0 top-[36px] -translate-y-1/2 pointer-events-none sm:hidden';
                  const railClsSm = 'absolute z-0 top-[40px] -translate-y-1/2 pointer-events-none hidden sm:block';
                  const segColor = resolveNodeColor(prevStep);
                  // Timer colour tracks the segment it floats above:
                  // failed → red, active → amber, not-reached → slate, done→done → primary
                  const timerColor =
                    step.status === 'failed' ? resolveNodeColor(step) :
                      step.status === 'active' ? APP_HEX.warning :
                        step.status === 'not-reached' ? APP_HEX.slate400 :
                          segColor;
                  const isDashed = step.status === 'active' || step.status === 'not-reached';
                  const dashColor = step.status === 'active' ? APP_HEX.warning : APP_HEX.slate400;
                  // Compute duration from dates; caller-supplied durationToNext overrides
                  const duration = prevStep?.durationToNext ?? calcDuration(prevStep.date, step.date);

                  return (
                    <>
                      {/* Duration badge — floats above the connector line (sm+ only) */}
                      {duration && (
                        <div
                          className="absolute z-20 hidden sm:flex justify-center items-center pointer-events-none"
                          style={{ top: '26px', left: segLeftSm, right: segRightSm }}
                        >
                          <span
                            className="inline-flex items-center gap-[3px] tabular-nums text-[10px] whitespace-nowrap"
                            style={{
                              color: timerColor,
                              fontWeight: 600,
                              letterSpacing: '0.02em',
                              lineHeight: 1,
                            }}
                          >
                            <FiClock className="w-[9px] h-[9px] shrink-0" strokeWidth={2.5} />
                            {duration}
                          </span>
                        </div>
                      )}

                      {/* Connector rail — mobile */}
                      {isDashed ? (
                        <div
                          className={`${railClsMobile} h-[3px] overflow-hidden`}
                          style={{ left: segLeftMobile, right: segRightMobile, borderTop: `3px dashed ${dashColor}88` }}
                        />
                      ) : (
                        <div
                          className={`${railClsMobile} h-[3px] rounded-full`}
                          style={{ left: segLeftMobile, right: segRightMobile, background: `linear-gradient(90deg, ${segColor}CC, ${nodeColor}CC)` }}
                        />
                      )}

                      {/* Connector rail — sm+ (web/tablet, shorter) */}
                      {isDashed ? (
                        <div
                          className={`${railClsSm} h-[3px] overflow-hidden`}
                          style={{ left: segLeftSm, right: segRightSm, borderTop: `3px dashed ${dashColor}88` }}
                        />
                      ) : (
                        <div
                          className={`${railClsSm} h-[3px] rounded-full`}
                          style={{ left: segLeftSm, right: segRightSm, background: `linear-gradient(90deg, ${segColor}CC, ${nodeColor}CC)` }}
                        />
                      )}
                    </>
                  );
                })()}

                {/* Node */}
                <div
                  className="relative z-10 flex flex-col items-center pt-1.5 overflow-visible"
                  style={{ flex: '1 1 0', minWidth: '56px' }}
                >
                  <div
                    className={`flex flex-col items-center select-none group ${step.detailsContent ? 'cursor-pointer' : 'cursor-default'}`}
                    style={{ '--step-color': isNotReached ? APP_HEX.slate : nodeColor } as React.CSSProperties}
                    onClick={() => step.detailsContent && handleClick(index)}
                  >
                    <Tooltip
                      content={step.tooltipContent}
                      placement="top"
                      variant="light"
                    >
                      <div className="relative flex-shrink-0">
                        {/* Persistent pulse ring + spinner for active step */}
                        {step.status === 'active' && showActiveAnimation && (
                          <>
                            <span
                              className="absolute inset-[-4px] rounded-full animate-ping opacity-20 pointer-events-none"
                              style={{ backgroundColor: APP_HEX.warning }}
                            />
                            <span
                              className="absolute inset-[-6px] rounded-full border-2 border-transparent animate-spin pointer-events-none"
                              style={{ borderTopColor: APP_HEX.warning, borderRightColor: `${APP_HEX.warning}44` }}
                            />
                          </>
                        )}

                        {/* Ping ring when open — uses pulse (not ping) to stay distinct from active */}
                        {isOpen && step.status !== 'active' && (
                          <span
                            className="absolute inset-[-5px] rounded-full animate-pulse opacity-25 pointer-events-none"
                            style={{ backgroundColor: nodeColor }}
                          />
                        )}

                        {/* Outer glow ring — hover preview (always mounted, CSS-only transition) */}
                        <div
                          className={`absolute inset-[-8px] rounded-full pointer-events-none transition-opacity duration-300 ease-out ${isOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'
                            }`}
                          style={{ background: `radial-gradient(circle, ${nodeColor}28 0%, transparent 65%)` }}
                        />

                        {/* Main circle */}
                        <div
                          className="w-12 h-12 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-[transform,box-shadow] duration-200 ease-out group-hover:scale-[1.08] will-change-transform"
                          style={{
                            backgroundColor: nodeColor,
                            boxShadow: isOpen
                              ? `0 0 0 4px ${nodeColor}40, 0 0 24px ${nodeColor}55, 0 6px 16px ${nodeColor}45`
                              : `0 0 0 3px ${nodeColor}28, 0 4px 14px ${nodeColor}40`,
                          }}
                        >
                          <span
                            className={`[&>svg]:w-6 [&>svg]:h-6 sm:[&>svg]:w-6 sm:[&>svg]:h-6 text-white transition-opacity duration-200 ease-out ${isNotReached ? 'opacity-50 group-hover:opacity-100' : ''}`}
                          >
                            {step.status === 'active' && showActiveClockIcon ? (
                              <FiClock className="text-white" strokeWidth={1.5} />
                            ) : (
                              step.iconElement ?? (
                                <span className="text-white font-bold text-lg">
                                  {index + 1}
                                </span>
                              )
                            )}
                          </span>
                        </div>

                        {/* Corner badge — done & failed only */}
                        {step.doubleTick ? (
                          <div
                            className="absolute -top-1 -right-1 w-4 h-4 sm:w-4 sm:h-4 rounded-full flex items-center justify-center shadow-lg"
                            style={{ background: step.doubleTickColor ?? nodeColor }}
                          >
                            <FaCheckDouble className="w-2 h-2 sm:w-2 sm:h-2 text-white" />
                          </div>
                        ) : step.statusBadge && step.status !== 'active' && step.status !== 'not-reached' ? (
                          <div
                            className={`absolute -top-1 -right-1 w-4 h-4 sm:w-4 sm:h-4 rounded-full flex items-center justify-center ${APP_BG.canvas} shadow border ${APP_BORDER.soft}`}
                          >
                            {step.statusBadge}
                          </div>
                        ) : step.status === 'done' ? (
                          <div
                            className="absolute -top-1 -right-1 w-4 h-4 sm:w-4 sm:h-4 rounded-full flex items-center justify-center shadow-md ring-[1.5px] ring-white dark:ring-black/60"
                            style={{ background: colors.primary }}
                          >
                            <FiCheck className="w-2 h-2 sm:w-3 sm:h-3 text-white" strokeWidth={3} />
                          </div>
                        ) : step.status === 'failed' ? (
                          <div
                            className="absolute -top-1 -right-1 w-4 h-4 sm:w-4 sm:h-4 rounded-full flex items-center justify-center shadow-md ring-[1.5px] ring-white dark:ring-black/60"
                            style={{ background: APP_VDI.failed }}
                          >
                            <FiAlertCircle className="w-2 h-2 sm:w-2 sm:h-2 text-white" />
                          </div>
                        ) : null}

                        {/* Expand / collapse hint — visible on hover (before click) and when open */}
                        {step.detailsContent && (
                          <div
                            className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-[2px] pointer-events-none"
                          >
                            {/* Connector dot */}
                            <div
                              className="w-[2px] h-[4px] rounded-full transition-opacity duration-200"
                              style={{
                                backgroundColor: nodeColor,
                                opacity: isOpen ? 0.9 : 0,
                              }}
                            />
                            {/* Chevron pill */}
                            <div
                              className={`flex items-center justify-center w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full shadow-sm transition-all duration-300 ease-out ring-1 ring-white dark:ring-black/50
                                ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-75 group-hover:opacity-60 group-hover:scale-95'}`}
                              style={{ backgroundColor: nodeColor }}
                            >
                              <FiChevronUp
                                className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-white transition-transform duration-300 ease-out"
                                style={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(180deg)' }}
                                strokeWidth={2.5}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </Tooltip>

                    {/* Label */}
                    <div className="mt-3 sm:mt-4 text-center px-1 w-full">
                      {step.title && (
                        <Text
                          as="p"
                          size="xs"
                          weight="bold"
                          color="soft"
                          leading="tight"
                          className={`line-clamp-2 group-hover:[color:var(--step-color)] transition-all duration-200 text-[10px] sm:text-xs ${isNotReached ? 'opacity-40 group-hover:opacity-100' : ''}`}
                        >
                          {step.title}
                        </Text>
                      )}
                      {step.subtitle && (
                        <Text
                          as="p"
                          size="xs"
                          weight="semibold"
                          leading="tight"
                          className={`mt-0.5 text-[9px] sm:text-[10px] tracking-wide transition-all duration-200 ${isNotReached ? 'opacity-30' : 'opacity-70'}`}
                          style={{ color: nodeColor }}
                        >
                          {step.subtitle}
                        </Text>
                      )}

                      {step.date && step.date !== '-' && (
                        <Text
                          as="p"
                          size="xs"
                          color="muted"
                          leading="tight"
                          className="mt-1 font-mono text-[9px] sm:text-xs"
                        >
                          {getDateTime(step.date)}
                        </Text>
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ── Status legend ── */}
      <div className="flex items-center justify-center flex-wrap gap-1.5 sm:gap-2 px-2 pb-1">
        {(legendItems ?? [
          { label: 'Done', color: colors.primary },
          { label: 'Active', color: APP_HEX.warning },
          { label: 'Failed', color: APP_VDI.failed },
          { label: 'Pending', color: APP_HEX.slate400 },
        ]).map(({ label, color }) => (
          <span
            key={label}
            className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[9px] sm:text-[10px] font-medium bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400"
          >
            <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
            {label}
          </span>
        ))}
      </div>

      {/* ── Detail panel ── */}
      {openIndex !== null && visibleSteps[openIndex]?.detailsContent && (
        <div
          className={`rounded-xl overflow-hidden border ${APP_BORDER.soft} ${APP_BG.canvas} shadow-xl`}
        >
          {/* Step-coloured accent bar */}
          <div
            className="h-[3px]"
            style={{
              background: `linear-gradient(90deg, ${resolveNodeColor(visibleSteps[openIndex])}, ${resolveNodeColor(visibleSteps[openIndex])}55)`,
            }}
          />
          {/* Panel header */}
          <div
            className={`flex items-center justify-between px-4 py-3 ${APP_BG.surface} border-b ${APP_BORDER.soft}`}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0"
                style={{
                  backgroundColor: resolveNodeColor(visibleSteps[openIndex]),
                }}
              >
                <span className="text-white [&>svg]:w-4 [&>svg]:h-4">
                  {visibleSteps[openIndex]?.iconElement}
                </span>
              </div>
              <div className="flex flex-col leading-none">
                <Text
                  as="span"
                  size="base"
                  weight="bold"
                  color="soft"
                  tracking="wider"
                  className="uppercase"
                >
                  {visibleSteps[openIndex]?.title}
                </Text>
                <Text as="span" size="xs" color="muted" className="mt-0.5">
                  {ELEMENTS_PROPS.STEPPER.ACTION_STATUS_BREAKDOWN}
                </Text>
              </div>
            </div>
            <AppButton
              variant="neutral"
              size="xs"
              shape="square"
              ripple={false}
              onClick={() => setOpenIndex(null)}
              icon={<FiX className="w-4 h-4" />}
            />
          </div>
          {/* Rows */}
          <div className={`px-4 py-3 divide-y ${APP_BORDER.divideDefault}`}>
            {visibleSteps[openIndex]?.detailsContent}
          </div>
        </div>
      )}
    </div>
  );
};
